import { Component, OnInit, inject } from '@angular/core';
import { Background } from '../../shared/components/background/background';
import { Connect } from '../../shared/components/connect/connect';
import { HeroSection } from '../../shared/components/hero-section/hero-section';
import { InfoCard } from '../../shared/components/info-card/info-card';
import { Button } from '../../shared/components/button/button';
import { ICaseStudiesType } from '../../shared/interfaces/case-studies.interface';
import { ActivatedRoute, Router } from '@angular/router';
import { ProjectDetails } from './components/project-details/project-details';
import { IDevelopmentProject } from '../../shared/interfaces/development-project.interface';
import { DevelopmentProjectsService } from '../../shared/services/development.projects.service';
import { DownloadInfo } from '../../shared/components/download-info/download-info';
import { MarketingProjectsService } from '../../shared/services/marketing.projects.service';
import { IMarketingProject } from '../../shared/interfaces/marketing-project.interface';
import { CardContainer } from "./components/card-container/card-container";
import { ICardInfoData } from '../../shared/interfaces/card-info.interface';
import { SeoService } from '../../shared/services/seo.service';

@Component({
  selector: 'app-case-studies-detail',
  imports: [Background, HeroSection, Connect, InfoCard, Button, ProjectDetails, DownloadInfo, CardContainer],
  templateUrl: './case-studies-detail.html',
  styleUrl: './case-studies-detail.scss'
})
export class CaseStudiesDetail implements OnInit {
  protected type: ICaseStudiesType | null = 'development';
  protected projectName: string | null = null;
  protected projectData: IDevelopmentProject | IMarketingProject | null = null;
  protected file = 'ai-development';

  private seo = inject(SeoService);

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private devProjectsService: DevelopmentProjectsService,
    private marketingProjectsService: MarketingProjectsService
  ) { }

  ngOnInit() {
    // Slug can come from either the :project route param (/case-studies/:project, /marketing/:project)
    // or from explicit route data (top-level WP-legacy slugs like /ualett, /resonante).
    this.route.data.subscribe(data => {
      const dataSlug = (data as Record<string, string | undefined>)['slug'];
      const dataType = (data as Record<string, string | undefined>)['type'] as 'development' | 'marketing' | undefined;

      this.route.paramMap.subscribe(params => {
        const projectParam = params.get('project') || dataSlug;

        if (!projectParam) {
          this.router.navigateByUrl('/');
          return;
        }

        // Type resolution: explicit route data wins, then URL prefix, then default development.
        const inferredType: 'development' | 'marketing' =
          dataType ??
          (this.router.url.startsWith('/case-studies/') ? 'development' : 'marketing');

        if (inferredType === 'development') {
          this.devProjectsService.getProjectByUri(projectParam)
            .subscribe(project => {
              this.projectData = project ?? null;
              if (!this.projectData) {
                this.router.navigateByUrl('/');
              } else {
                this.type = 'development';
                this.applySeo(projectParam, this.projectData as IDevelopmentProject, 'development');
              }
            });
        } else {
          this.marketingProjectsService.getProjectByUri(projectParam)
            .subscribe(project => {
              this.projectData = project ?? null;
              if (!this.projectData) {
                this.router.navigateByUrl('/');
              } else {
                this.type = 'marketing';
                this.applySeo(projectParam, this.projectData as IMarketingProject, 'marketing');
              }
            });
        }
      });
    });
  }

  private applySeo(slug: string, project: IDevelopmentProject | IMarketingProject, type: 'development' | 'marketing') {
    // Marketing case studies prefer top-level slugs (e.g. /ualett/) to preserve the
    // original WordPress URLs that Google has indexed. /marketing/:project is still
    // routable as a secondary; canonical points at the top-level.
    const path = type === 'development' ? `/case-studies/${slug}/` : `/${slug}/`;
    const url = `https://semnexus.com${path}`;
    const image = (project.images && project.images.length > 0) ? project.images[0] : project.logo;

    this.seo.apply({
      title: `${project.name} Case Study`,
      description: project.description.slice(0, 160),
      canonical: path,
      image,
      imageAlt: `${project.name} app — case study`,
      type: 'website',
    });

    this.seo.setBreadcrumbs([
      { name: 'Home', url: '/' },
      { name: type === 'development' ? 'Development Case Studies' : 'Marketing Case Studies',
        url: type === 'development' ? '/development-case-studies/' : '/marketing-case-studies/' },
      { name: project.name },
    ]);

    this.seo.setJsonLd('case-study', {
      '@context': 'https://schema.org',
      '@type': 'CreativeWork',
      '@id': `${url}#case-study`,
      name: `${project.name} — case study`,
      headline: project.name,
      description: project.description,
      image: [image],
      url,
      author: { '@id': 'https://semnexus.com/#organization' },
      publisher: { '@id': 'https://semnexus.com/#organization' },
      about: { '@type': 'SoftwareApplication', name: project.name, applicationCategory: 'MobileApplication' },
      mainEntityOfPage: { '@type': 'WebPage', '@id': url },
    });
  }

  goToCaseStudies() {
    const redirect = this.type === 'development' ? 'development-case-studies' : 'marketing-case-studies'
    this.router.navigate([redirect]);
  }

  getProjectCards(): ICardInfoData[] {
    if (this.type === 'marketing') {
      const project = this.projectData as IMarketingProject
      return [{
        icon: 'images/shared/cards/screw.png',
        title: 'our strategy',
        text: project.strategy || ''
      },
      {
        icon: 'images/shared/cards/bag.png',
        title: 'app store optimization',
        text: project.optimization || ''
      }, {
        icon: 'images/shared/cards/bulb.png',
        title: 'ad creative',
        text: project.creative || ''
      }]
    }
    else if (this.type === 'development') {
      const project = this.projectData as IDevelopmentProject

      return [{
        icon: 'images/shared/cards/rocket.png',
        title: 'discovery',
        text: project.discovery || ''
      },
      {
        icon: 'images/shared/cards/bulb.png',
        title: 'design',
        text: project.design || ''
      }, {
        icon: 'images/shared/cards/plant.png',
        title: 'development',
        text: project.development || ''
      }]
    }
    return []
  }
}
